import { ProductBulkActionType, productApis } from "@payping/api";
import { Archive, Bolt, BoltOff, Delete, UnArchiveOutlined } from "@payping/icons";
import { Checkbox, Modal, Space, Toast, Typography } from "@payping/web-components";
import React from "react";
import { useAsyncFn } from "react-use";

import { useAppTranslation } from "@/utils/useAppTranslation";
import { ListCardProps } from "@payping/web-components/components/List/types";

interface Props {
  retry: () => void;
  isArchived?: boolean;
  selectedRowKeys: string[];
  setSelectedRowKeys: React.Dispatch<React.SetStateAction<string[]>>;
  data: ListCardProps["dataSource"];
}

export const ListBulkActions = (props: Props) => {
  const { setSelectedRowKeys, data = [], selectedRowKeys, isArchived, retry } = props;
  const { t } = useAppTranslation();
  const [modal, contextHolder] = Modal.useModal();

  const allRowsKey = data.map((row) => String(row.key));
  const isAllRowsKeySelected = allRowsKey.length === selectedRowKeys.length;

  const [{ loading: loadingPaidProductsCodes }, getPaidProductsCodes] = useAsyncFn(
    () =>
      productApis.productCheckCodesHadPayment({
        bulkActionType: ProductBulkActionType.CheckCodesHadPayment,
        codes: selectedRowKeys,
      }),
    [selectedRowKeys]
  );

  async function handleBulkAction(finalSelectedRowKeys: string[], bulkActionType: ProductBulkActionType) {
    switch (bulkActionType) {
      case ProductBulkActionType.Archive:
      case ProductBulkActionType.DeArchive:
        await productApis.productBulkChangeArchive({ bulkActionType, codes: finalSelectedRowKeys });
        break;
      case ProductBulkActionType.Activate:
      case ProductBulkActionType.Deactivate:
        await productApis.productBulkChangeActivation({ bulkActionType, codes: finalSelectedRowKeys });
        break;
      case ProductBulkActionType.Delete:
        await productApis.productBulkDelete({ bulkActionType, codes: finalSelectedRowKeys });
        break;
      default:
        break;
    }
    // Clear all selections after operation
    setSelectedRowKeys([]);
    retry();
  }

  async function onBulkActionClick(bulkActionType: ProductBulkActionType): Promise<void> {
    let confirmText = t(`product.confirm.${ProductBulkActionType[bulkActionType]}`);
    // selectedRowKeys can be updated so we need to pass the updated values to callBulkAction
    let finalSelectedRowKeys = selectedRowKeys;
    if (bulkActionType === ProductBulkActionType.Delete) {
      // if product is paid it cannot be deleted
      const paidProductsCodes = await getPaidProductsCodes();
      if (paidProductsCodes.length) {
        confirmText = t("product.confirm.deleteSome", { codes: paidProductsCodes.join(", ") });
        const notPaidProducts = selectedRowKeys.filter(
          (selectedCode) => !paidProductsCodes.find((paidProductCode) => paidProductCode === selectedCode)
        );
        finalSelectedRowKeys = notPaidProducts;
        setSelectedRowKeys(notPaidProducts);
      }
    }
    // if there is anything to delete
    if (finalSelectedRowKeys.length) {
      modal.confirm({
        cancelText: t("cancel"),
        content: confirmText,
        maskClosable: true,
        okText: t("ok"),
        onOk: async () => handleBulkAction(finalSelectedRowKeys, bulkActionType),
      });
    } else {
      modal.error({ content: t("product.confirm.deleteNone") });
    }
  }

  return (
    <Toast visible={Boolean(selectedRowKeys.length)} loading={loadingPaidProductsCodes}>
      <Space size={"small"} className={"tw-w-full"} direction={"vertical"}>
        <div className={"tw-flex tw-justify-start"}>
          <Toast.Item>
            <Space size={"small"}>
              <Checkbox
                onClick={() => {
                  if (isAllRowsKeySelected) {
                    setSelectedRowKeys([]);
                    return;
                  }
                  setSelectedRowKeys(allRowsKey);
                }}
                checked={isAllRowsKeySelected}
                indeterminate={!isAllRowsKeySelected && selectedRowKeys.length > 0}
              >
                <Typography.Text type={"secondary"}>{t("common.operation.selectAll")}</Typography.Text>
              </Checkbox>
              <Typography.Text type={"secondary"}>{`${selectedRowKeys.length}${t(
                "common.selectedItem"
              )}`}</Typography.Text>
            </Space>
          </Toast.Item>
        </div>
        <Space>
          {isArchived ? (
            <Toast.Item
              label={t("common.operation.unArchive")}
              Icon={() => <UnArchiveOutlined width={16} />}
              onClick={() => onBulkActionClick(ProductBulkActionType.DeArchive)}
            />
          ) : (
            <Toast.Item
              label={t("common.operation.archive")}
              Icon={() => <Archive width={16} />}
              onClick={() => onBulkActionClick(ProductBulkActionType.Archive)}
            />
          )}
          <Toast.Item
            label={t("common.operation.activate")}
            Icon={() => <Bolt width={16} />}
            onClick={() => onBulkActionClick(ProductBulkActionType.Activate)}
          />
          <Toast.Item
            label={t("common.operation.deactivate")}
            Icon={() => <BoltOff width={16} />}
            onClick={() => onBulkActionClick(ProductBulkActionType.Deactivate)}
          />
          <Toast.Item
            label={t("common.operation.delete")}
            Icon={() => <Delete width={16} />}
            onClick={() => onBulkActionClick(ProductBulkActionType.Delete)}
          />
        </Space>
      </Space>
      {contextHolder}
    </Toast>
  );
};
